import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users, GraduationCap, TrendingUp, TrendingDown, Award, Loader2,
  Target, Clock, AlertTriangle, Trophy, Zap, ArrowRight, Filter,
  Sparkles, FileDown, X,
} from "lucide-react";

const GRADE_LABEL = { "4": "4°", "5": "5°", "6": "6°", "7": "7°", "8": "8°", "9": "9°", "1-bach": "1° Bach", "2-bach": "2° Bach" };
const SUBJECT_LABEL = {
  "matematica-datos": "Matemática y Datos",
  "comunicacion-lenguaje": "Comunicación y Lenguaje",
  "ciencia-tecnologia": "Ciencia y Tecnología",
  "ciudadania-valores": "Ciudadanía y Valores",
};
const RISK_COLOR = {
  bajo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medio: "bg-amber-50 text-amber-700 border-amber-200",
  alto: "bg-red-50 text-red-700 border-red-200",
};
const ALERT_COLOR = {
  bajo: "bg-emerald-50 border-emerald-200 text-emerald-900",
  medio: "bg-amber-50 border-amber-200 text-amber-900",
  alto: "bg-red-50 border-red-200 text-red-900",
};

/**
 * InstitutionAnalyticsPanel — Fase 1
 *
 * Dashboard analítico con filtros dinámicos (período/docente/grado/materia),
 * KPIs con tendencia vs período anterior, radar Bloom, progreso por materia,
 * estudiantes inactivos con nivel de riesgo, ranking (participación +
 * crecimiento), alertas inteligentes con semáforo.
 *
 * Backend: POST /api/institution/analytics
 * Filtros: GET  /api/institution/filter-options
 */
export default function InstitutionAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState("");  // "", "executive", "direction", "council"
  const [actionError, setActionError] = useState("");
  const [filters, setFilters] = useState({
    period: "month",
    custom_start: "",
    custom_end: "",
    teacher_id: "",
    grade: "",
    subject: "",
  });
  const [options, setOptions] = useState({ teachers: [], grades: [], subjects: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = { ...filters };
      // Solo enviar custom_start/end si el período es 'custom' y ambos están seteados
      if (body.period !== "custom") {
        delete body.custom_start;
        delete body.custom_end;
      } else if (!body.custom_start || !body.custom_end) {
        // Si custom pero falta rango, no dispara la request
        setLoading(false);
        return;
      }
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      const r = await axios.post(`${API}/institution/analytics`, body, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      console.warn("analytics fail", e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    axios.get(`${API}/institution/filter-options`, { withCredentials: true })
      .then((r) => setOptions(r.data))
      .catch((e) => console.warn("filter-options fail", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  const generateStrategicPlan = useCallback(async () => {
    if (!data) return;
    setPlanLoading(true);
    setActionError("");
    try {
      const r = await axios.post(
        `${API}/institution/strategic-plan`,
        { analytics: data },
        { withCredentials: true, timeout: 120000 },
      );
      setPlan(r.data.plan);
      setPlanOpen(true);
    } catch (e) {
      setActionError(e?.response?.data?.detail || "No pudimos generar el plan estratégico. Intenta de nuevo.");
    } finally {
      setPlanLoading(false);
    }
  }, [data]);

  const downloadExecutivePdf = useCallback(async () => {
    if (!data) return;
    setPdfLoading("executive");
    setActionError("");
    try {
      const r = await axios.post(
        `${API}/institution/reports/executive`,
        { analytics: data, plan },
        { withCredentials: true, responseType: "blob", timeout: 60000 },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `QHUB_Ejecutivo_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      // Decode blob error body so backend detail (400/403/500) surfaces to user
      let detail = "";
      const blobErr = e?.response?.data;
      if (blobErr instanceof Blob) {
        try { detail = JSON.parse(await blobErr.text())?.detail || ""; } catch { /* keep default */ }
      } else {
        detail = blobErr?.detail || "";
      }
      setActionError(detail || "No pudimos generar el PDF. Intenta de nuevo.");
    } finally {
      setPdfLoading("");
    }
  }, [data, plan]);

  const downloadReport = useCallback(async (kind) => {
    if (!data) return;
    const endpoints = {
      executive: { url: "reports/executive", file: "QHUB_Ejecutivo" },
      direction: { url: "reports/academic-direction", file: "QHUB_DireccionAcademica" },
      council: { url: "reports/academic-council", file: "QHUB_ConsejoAcademico" },
    };
    const cfg = endpoints[kind];
    if (!cfg) return;
    setPdfLoading(kind);
    setActionError("");
    try {
      const r = await axios.post(
        `${API}/institution/${cfg.url}`,
        { analytics: data, plan },
        { withCredentials: true, responseType: "blob", timeout: 90000 },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `${cfg.file}_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      let detail = "";
      const blobErr = e?.response?.data;
      if (blobErr instanceof Blob) {
        try { detail = JSON.parse(await blobErr.text())?.detail || ""; } catch { /* keep */ }
      } else {
        detail = blobErr?.detail || "";
      }
      setActionError(detail || "No pudimos generar el PDF. Intenta de nuevo.");
    } finally {
      setPdfLoading("");
    }
  }, [data, plan]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="institution-analytics-panel">
      {/* ── Filtros ─────────────────────────────────────────────── */}
      <FiltersBar
        filters={filters}
        options={options}
        onChange={(next) => setFilters((p) => ({ ...p, ...next }))}
        onReset={() => setFilters({ period: "month", custom_start: "", custom_end: "", teacher_id: "", grade: "", subject: "" })}
        loading={loading}
      />

      {/* ── Acciones IA / Reportes ─────────────────────────────── */}
      <div
        className="flex flex-wrap items-center gap-3 bg-gradient-to-r from-purple-50 via-blue-50 to-emerald-50 border border-purple-100 rounded-2xl p-4"
        data-testid="analytics-actions"
      >
        <div className="flex items-start gap-3 flex-1 min-w-[240px]">
          <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-gray-800">Insights institucionales con IA</p>
            <p className="text-xs text-gray-600">
              Genera un plan estratégico mensual con GPT-4.1 o descarga los informes: Ejecutivo (2-4 pg), Dirección Académica (8-15 pg) o Consejo Académico (10-20 pg).
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={generateStrategicPlan}
            disabled={planLoading || !data}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="btn-strategic-plan"
          >
            {planLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generar Plan Estratégico Mensual
          </Button>
          <Button
            onClick={() => downloadReport("executive")}
            disabled={pdfLoading === "executive" || !data}
            variant="outline"
            data-testid="btn-executive-pdf"
          >
            {pdfLoading === "executive" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            PDF Ejecutivo
          </Button>
          <Button
            onClick={() => downloadReport("direction")}
            disabled={pdfLoading === "direction" || !data}
            variant="outline"
            data-testid="btn-direction-pdf"
          >
            {pdfLoading === "direction" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Dirección Académica
          </Button>
          <Button
            onClick={() => downloadReport("council")}
            disabled={pdfLoading === "council" || !data}
            variant="outline"
            data-testid="btn-council-pdf"
          >
            {pdfLoading === "council" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Consejo Académico
          </Button>
        </div>
      </div>
      {actionError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="analytics-action-error">
          {actionError}
        </div>
      )}

      {/* ── KPIs ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <Kpi icon={GraduationCap} label="Estudiantes en scope" value={k.total_students} color="text-blue-600" />
        <Kpi icon={Users} label="Estudiantes activos" value={k.active_students.value} trend={k.active_students.trend} color="text-emerald-600" />
        <Kpi icon={Users} label="Inactivos" value={k.inactive_students.value} trend={k.inactive_students.trend} invertTrend color="text-orange-600" />
        <Kpi icon={Target} label="% Participación" value={`${k.participation_pct.value}%`} trend={k.participation_pct.trend} color="text-purple-600" />
        <Kpi icon={Zap} label="Evidencias" value={k.evidences.value} trend={k.evidences.trend} color="text-amber-600" />
        <Kpi icon={Award} label="Puntos" value={k.points.value} trend={k.points.trend} color="text-fuchsia-600" />
        <Kpi icon={TrendingUp} label="Nivel Bloom prom." value={`${k.avg_bloom_level.value}/6`} trend={k.avg_bloom_level.trend} color="text-cyan-600" />
        <Kpi icon={Clock} label="Tiempo estimado uso" value={`${k.avg_activity_minutes.value} min`} trend={k.avg_activity_minutes.trend} color="text-indigo-600" hint="proxy: 3 min/evidencia" />
      </div>

      {/* ── Alertas inteligentes ───────────────────────────────── */}
      {data.alerts && data.alerts.length > 0 && (
        <div className="space-y-2" data-testid="analytics-alerts">
          {data.alerts.map((a, i) => (
            <div
              key={`${a.type}-${i}`}
              data-testid={`alert-${a.type}`}
              className={`flex items-start gap-3 p-3 rounded-xl border ${ALERT_COLOR[a.severity]}`}
            >
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{a.title}</p>
                <p className="text-xs mt-1 opacity-80">{a.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Radar Bloom + Progreso por materia ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="bloom-radar">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-700">Nivel Bloom del período</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={data.bloom_distribution} outerRadius={90}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="level" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis tick={{ fontSize: 10 }} />
              <Radar name="Evidencias" dataKey="count" stroke="#a78bfa" fill="#a78bfa" fillOpacity={0.35} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-3 gap-1 mt-2">
            {data.bloom_distribution.map((b) => (
              <div key={b.level} className="text-[10px] text-gray-500 text-center">
                <div className="font-semibold">{b.level}</div>
                <div>{b.count} ({b.pct}%)</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="subject-progress">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-emerald-500" />
            <h3 className="text-sm font-semibold text-gray-700">Progreso por materia (indicadores)</h3>
          </div>
          {data.subject_progress.length === 0 ? (
            <p className="text-xs text-gray-500 py-10 text-center">Aún no hay evidencias en el período.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.subject_progress.map((s) => ({
                name: (SUBJECT_LABEL[s.subject] || s.subject).replace(" y ", "\ny "),
                Alcanzados: s.achieved,
                Pendientes: s.pending,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Alcanzados" stackId="a" fill="#34d399" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Pendientes" stackId="a" fill="#e2e8f0" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Ranking ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankingCard
          title="Top participación"
          icon={Trophy}
          iconColor="text-amber-500"
          testId="ranking-participation"
          rows={data.ranking_participation}
          renderRow={(r) => (
            <>
              <span className="text-xs text-gray-500">{r.evidences} evid. · {r.points} pts</span>
            </>
          )}
        />
        <RankingCard
          title="Mayor crecimiento (puntos)"
          icon={ArrowRight}
          iconColor="text-emerald-500"
          testId="ranking-growth"
          rows={data.ranking_growth}
          renderRow={(r) => (
            <>
              <span className="text-xs text-gray-500">
                {r.prev_points} → {r.curr_points} <span className="text-emerald-600 font-semibold">(+{r.delta})</span>
              </span>
            </>
          )}
        />
      </div>

      {/* ── Estudiantes sin actividad ──────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="inactive-students">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          <h3 className="text-sm font-semibold text-gray-700">
            Estudiantes sin actividad reciente ({data.inactive_students.length})
          </h3>
        </div>
        {data.inactive_students.length === 0 ? (
          <p className="text-xs text-gray-500 py-6 text-center">
            Todos los estudiantes registran actividad en los últimos 7 días.
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estudiante</TableHead>
                  <TableHead>Grado</TableHead>
                  <TableHead>Último acceso</TableHead>
                  <TableHead className="text-right">Días inactivo</TableHead>
                  <TableHead className="text-right">Riesgo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.inactive_students.map((s) => (
                  <TableRow key={s.profile_id} data-testid={`inactive-row-${s.profile_id}`}>
                    <TableCell className="font-medium">{s.student_name}</TableCell>
                    <TableCell><Badge variant="outline">{GRADE_LABEL[s.grade] || s.grade}</Badge></TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {s.last_activity ? new Date(s.last_activity).toLocaleDateString("es-SV") : "Nunca"}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {s.days_inactive != null ? s.days_inactive : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${RISK_COLOR[s.risk]}`}>
                        {s.risk.toUpperCase()}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* ── Modal Plan Estratégico ─────────────────────────────── */}
      {planOpen && plan && (
        <StrategicPlanModal
          plan={plan}
          onClose={() => setPlanOpen(false)}
          onDownloadPdf={() => downloadReport("executive")}
          pdfLoading={pdfLoading === "executive"}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Sub-componentes internos
// ══════════════════════════════════════════════════════════════════════

function FiltersBar({ filters, options, onChange, onReset, loading }) {
  const isCustom = filters.period === "custom";
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="analytics-filters">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 text-gray-700 mr-2 pb-2">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-semibold">Filtros</span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Período</span>
          <Select value={filters.period} onValueChange={(v) => onChange({ period: v })}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Semana</SelectItem>
              <SelectItem value="month">Mes</SelectItem>
              <SelectItem value="quarter">Trimestre</SelectItem>
              <SelectItem value="year">Año</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isCustom && (
          <>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">Desde</span>
              <input
                type="date"
                value={filters.custom_start || ""}
                max={filters.custom_end || today}
                onChange={(e) => onChange({ custom_start: e.target.value })}
                className="h-9 rounded-md border border-gray-200 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="filter-custom-start"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-gray-500 uppercase tracking-wide">Hasta</span>
              <input
                type="date"
                value={filters.custom_end || ""}
                min={filters.custom_start || undefined}
                max={today}
                onChange={(e) => onChange({ custom_end: e.target.value })}
                className="h-9 rounded-md border border-gray-200 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="filter-custom-end"
              />
            </div>
          </>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Docente</span>
          <Select value={filters.teacher_id || "all"} onValueChange={(v) => onChange({ teacher_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="filter-teacher"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {options.teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Grado</span>
          <Select value={filters.grade || "all"} onValueChange={(v) => onChange({ grade: v === "all" ? "" : v })}>
            <SelectTrigger className="h-9 w-[120px]" data-testid="filter-grade"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {options.grades.map((g) => (
                <SelectItem key={g} value={g}>{GRADE_LABEL[g] || g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Materia</span>
          <Select value={filters.subject || "all"} onValueChange={(v) => onChange({ subject: v === "all" ? "" : v })}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="filter-subject"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {options.subjects.map((s) => (
                <SelectItem key={s} value={s}>{SUBJECT_LABEL[s] || s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto flex items-center gap-2 pb-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
          <Button variant="outline" size="sm" onClick={onReset} data-testid="filter-reset">Reset</Button>
        </div>
      </div>

      {isCustom && (!filters.custom_start || !filters.custom_end) && (
        <p className="mt-3 text-xs text-amber-600 flex items-center gap-1" data-testid="filter-custom-hint">
          <AlertTriangle className="w-3 h-3" />
          Selecciona un rango de fechas <b>Desde</b> y <b>Hasta</b> para actualizar los datos.
        </p>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, trend, color, hint, invertTrend }) {
  const pct = trend?.pct;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  const isPositive = invertTrend ? down : up;
  const isNegative = invertTrend ? up : down;
  const trendColor = isPositive ? "text-emerald-600" : isNegative ? "text-red-500" : "text-gray-400";
  const TrendIcon = up ? TrendingUp : (down ? TrendingDown : null);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-gray-500 font-medium truncate">{label}</span>
        {Icon && <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />}
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {trend && pct != null && (
        <div className={`text-[11px] font-medium flex items-center gap-1 ${trendColor}`}>
          {TrendIcon && <TrendIcon className="w-3 h-3" />}
          <span>{pct > 0 ? "+" : ""}{pct}%</span>
          <span className="text-gray-400 font-normal">vs período anterior</span>
        </div>
      )}
      {hint && <div className="text-[10px] text-gray-400">{hint}</div>}
    </div>
  );
}

function RankingCard({ title, icon: Icon, iconColor, rows, renderRow, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 py-6 text-center">Sin datos en el período seleccionado.</p>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.profile_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                  i === 0 ? "bg-amber-100 text-amber-700" :
                  i === 1 ? "bg-gray-100 text-gray-700" :
                  i === 2 ? "bg-orange-100 text-orange-700" : "bg-gray-50 text-gray-500"
                }`}>{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{r.student_name}</p>
                  <p className="text-[10px] text-gray-500">{GRADE_LABEL[r.grade] || r.grade}</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">{renderRow(r)}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}


function StrategicPlanModal({ plan, onClose, onDownloadPdf, pdfLoading }) {
  const priorityBadge = (p) => {
    const key = (p || "media").toLowerCase();
    const styles = {
      alta: "bg-red-100 text-red-800 border-red-200",
      media: "bg-amber-100 text-amber-800 border-amber-200",
      baja: "bg-emerald-100 text-emerald-800 border-emerald-200",
    };
    return `text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${styles[key] || styles.media}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="strategic-plan-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 p-5 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-base font-bold text-gray-900">Plan Estratégico Mensual</h2>
            <Badge variant="outline" className="text-[10px]">GPT-4.1</Badge>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
            data-testid="close-strategic-plan"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {plan.executive_summary && (
            <section data-testid="plan-summary">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Resumen ejecutivo</h3>
              <p className="text-sm text-gray-800 leading-relaxed">{plan.executive_summary}</p>
            </section>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plan.strengths?.length > 0 && (
              <section data-testid="plan-strengths" className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                <h3 className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-2">Fortalezas</h3>
                <ul className="space-y-1 text-sm text-emerald-900 list-disc pl-4">
                  {plan.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </section>
            )}
            {plan.risks?.length > 0 && (
              <section data-testid="plan-risks" className="bg-red-50 border border-red-100 rounded-xl p-3">
                <h3 className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-2">Riesgos</h3>
                <ul className="space-y-1 text-sm text-red-900 list-disc pl-4">
                  {plan.risks.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </section>
            )}
          </div>

          {plan.smart_goals?.length > 0 && (
            <section data-testid="plan-smart-goals">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Objetivos SMART</h3>
              <div className="space-y-2">
                {plan.smart_goals.map((g, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-3 bg-gray-50">
                    <p className="text-sm font-semibold text-gray-900">{g.title}</p>
                    {g.metric && <p className="text-xs text-gray-700 mt-1"><b>Métrica:</b> {g.metric}</p>}
                    {g.deadline && <p className="text-xs text-gray-600"><b>Plazo:</b> {g.deadline}</p>}
                    {g.why && <p className="text-xs text-gray-600 mt-1 italic">{g.why}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {plan.action_plan?.length > 0 && (
            <section data-testid="plan-actions">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Plan de acción</h3>
              <ol className="space-y-2">
                {plan.action_plan.map((a, i) => (
                  <li key={i} className="border border-gray-100 rounded-xl p-3 flex items-start gap-3">
                    <span className="text-sm font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}.</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={priorityBadge(a.priority)}>{a.priority || "media"}</span>
                        {a.responsible && <span className="text-[11px] text-gray-500">→ {a.responsible}</span>}
                      </div>
                      <p className="text-sm text-gray-900">{a.action}</p>
                      {a.expected_impact && (
                        <p className="text-xs text-emerald-700 mt-1"><b>Impacto:</b> {a.expected_impact}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {plan.prediction && (
            <section data-testid="plan-prediction" className="bg-purple-50 border border-purple-100 rounded-xl p-3">
              <h3 className="text-xs font-semibold text-purple-800 uppercase tracking-wide mb-2">Predicción próximo mes</h3>
              {plan.prediction.probability_pct != null && (
                <p className="text-sm text-purple-900 font-semibold">
                  Probabilidad de éxito si se ejecuta el plan: {plan.prediction.probability_pct}%
                </p>
              )}
              {plan.prediction.expected_next_month && (
                <p className="text-sm text-purple-900 mt-1">{plan.prediction.expected_next_month}</p>
              )}
              {plan.prediction.risk_if_no_action && (
                <p className="text-xs text-purple-800 mt-2 italic"><b>Si no se actúa:</b> {plan.prediction.risk_if_no_action}</p>
              )}
            </section>
          )}
        </div>

        <div className="border-t border-gray-100 p-4 flex flex-wrap items-center justify-end gap-2 sticky bottom-0 bg-white rounded-b-2xl">
          <Button variant="outline" onClick={onClose} data-testid="btn-close-plan">Cerrar</Button>
          <Button
            onClick={onDownloadPdf}
            disabled={pdfLoading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="btn-download-with-plan"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Descargar PDF con este plan
          </Button>
        </div>
      </div>
    </div>
  );
}
